import React from 'react';

const STORAGE_KEY_RETAILER = 'campaigns_retailer_id';
const STORAGE_KEY_CYCLE = 'campaigns_cycle_id';

function readStoredId(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredId(key: string, value: number | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, String(value));
    }
  } catch {
    // Storage may be unavailable (private mode, quota exceeded, etc.)
  }
}

export type CampaignFilters = {
  retailerId: number | null;
  cycleId: number | null;
  setRetailerId: (id: number | null) => void;
  setCycleId: (id: number | null) => void;
};

const CampaignFiltersContext = React.createContext<CampaignFilters | undefined>(undefined);

export function CampaignFiltersProvider({ children }: { children: React.ReactNode }) {
  const [retailerId, setRetailerIdRaw] = React.useState<number | null>(() => readStoredId(STORAGE_KEY_RETAILER));
  const [cycleId, setCycleIdRaw] = React.useState<number | null>(() => readStoredId(STORAGE_KEY_CYCLE));

  const setRetailerId = React.useCallback((id: number | null) => {
    writeStoredId(STORAGE_KEY_RETAILER, id);
    setRetailerIdRaw(id);
  }, []);

  const setCycleId = React.useCallback((id: number | null) => {
    writeStoredId(STORAGE_KEY_CYCLE, id);
    setCycleIdRaw(id);
  }, []);

  const value = React.useMemo(() => ({ retailerId, cycleId, setRetailerId, setCycleId }), [retailerId, cycleId, setRetailerId, setCycleId]);
  return <CampaignFiltersContext.Provider value={value}>{children}</CampaignFiltersContext.Provider>;
}

export function useCampaignFilters(): CampaignFilters {
  const ctx = React.useContext(CampaignFiltersContext);
  if (!ctx) throw new Error('useCampaignFilters must be used within CampaignFiltersProvider');
  return ctx;
}


